import React, { useState, useEffect, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Slide, ToastContainer, toast } from 'react-toastify';
import classNames from 'classnames';
import UploadBlack from '../Icons/UploadBlack';
import FilesList from './FilesList';
import CloseIcon from '../Icons/CloseIcon';
import { getAllowedFileTypes, getMetTags } from './HttpServices';
import 'react-toastify/dist/ReactToastify.css';
import appConfig from '../../appConfig';
import Error from '../Icons/Error';
import {
  FILE_NAME_AND_TYPES,
  FILE_REJECTION_ERROR_CODE,
  TOAST_TYPE,
} from './constants';
import FilesLoadingIndicator from './FilesLoadingIndicator';
import { useDropzone } from 'react-dropzone';
import {
  hideCategoryFileUploader,
  maximizeFileUploader,
  minimizeFileUploader,
} from '../../actions/mainUI';
import { getFileExtension } from './utils';

function FilesUploader({ homeScreenRefresh, intl }) {
  const [images, setImages] = useState([]);
  const [isDropAreaVisible, setIsDropAreaVisible] = useState(false);
  const [metaTags, setMetaTags] = useState([]);
  const [allowedFileTypes, setAllowedFileTypes] = useState([]);
  const [isStartUpLoading, setIsStartUpLoading] = useState(false);
  const hstPath = sessionStorage.getItem('destination');
  const csrf = sessionStorage.getItem('token');
  const [isUploading, setIsUploading] = useState(false);
  const [completedApiCount, setCompletedApiCount] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isFilesUploaded, setIsFilesUploaded] = useState(false);
  const [metaValues, setMetaValues] = useState([]);
  const [isJobProcessing, setIsJobProcessing] = useState(false);
  const [isAllJobsFinished, setIsAllJobsFinished] = useState([]);
  const [filesLoadingStatus, setFilesLoadingStatus] = useState([]);
  const [isUploadingIndicatorShown, setIsUploadingIndicatorShown] =
    useState(false);
  const [updatingFileIndex, setUpdatingFileIndex] = useState(null);
  const dispatch = useDispatch();
  const collectionId = useSelector((state) => {
    return state.mainUI.get('collectionIdForEdit');
  });
  const categoryIdToUpload = useSelector((state) => {
    return state.mainUI.get('categoryToUpload').categoryID;
  });
  const categoryNameToUpload = useSelector((state) => {
    return state.mainUI.get('categoryToUpload').categoryName;
  });
  const isMinimized = useSelector((state) => {
    return state.mainUI.getIn(['fileUploader', 'isMinimized']);
  });
  const isCollapsed = useSelector((state) => {
    return state.mainUI.getIn(['fileUploader', 'isCollapsed']);
  });
  const isSurlCookie = useSelector((state) => {
    return state.session.get('surlCookie');
  });
  const surlFromSession = useSelector((state) => {
    return state.session.get('surl');
  });

  const surl = (isSurlCookie && '') || surlFromSession;

  useEffect(() => {
    setImages([]);
    setMetaValues([]);
    setCompletedApiCount([]);
    setFilesLoadingStatus([]);
    setIsError(false);
    setIsJobProcessing(false);
    setIsUploadingIndicatorShown(false);
    if (isMinimized || isCollapsed) dispatch(maximizeFileUploader());
    setIsUploading(false);
  }, [categoryIdToUpload]);

  useEffect(() => {
    if (isFilesUploaded) {
      setMetaValues([]);
      setCompletedApiCount([]);
    }
  }, [isFilesUploaded]);

  useEffect(() => {
    if ((isJobProcessing || isUploadingIndicatorShown) && !isMinimized) {
      dispatch(minimizeFileUploader());
    }
  }, [isJobProcessing, isUploadingIndicatorShown]);

  const initializeUploader = async () => {
    try {
      setIsStartUpLoading(true);
      FILE_NAME_AND_TYPES.forEach(async ([fileType, fileName]) => {
        const result = await getAllowedFileTypes(
          hstPath,
          categoryIdToUpload,
          surl,
          csrf,
          fileType,
          fileName
        );
        if (result == null) {
          setIsError(true);
        } else {
          const extensions = result
            ?.map((filename) => getFileExtension(filename))
            .filter(Boolean);
          setAllowedFileTypes((prevState) => [...prevState, ...extensions]);
        }
      });
      const metaTags = await getMetTags(
        hstPath,
        collectionId,
        surl,
        csrf,
        setMetaTags,
        setIsStartUpLoading
      );
      if (metaTags) {
        setMetaTags(Array.isArray(metaTags) ? metaTags : []);
        setIsStartUpLoading(false);
      } else {
        setIsError(true);
      }
    } catch (e) {
      setIsStartUpLoading(false);
      setIsError(true);
    }
  };

  useEffect(() => {
    initializeUploader();
    return () => {
      // dispatch(showCategoryFileUploader(null, null, false));
    };
  }, [categoryIdToUpload]);

  useEffect(() => {
    if (!isDropAreaVisible) {
      setIsDropAreaVisible(true);
    }
    return () => images.forEach((file) => URL.revokeObjectURL(file.data_url));
  }, []);

  useEffect(() => {
    if (images.length == 0) {
      setIsDropAreaVisible(true);
    } else if (isDropAreaVisible && images.length !== 0)
      setIsDropAreaVisible(false);
  }, [images.length]);

  const showToast = (message, toast_type) => {
    const toastParam = {
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
      transition: Slide,
    };
    switch (toast_type) {
      case TOAST_TYPE.ERROR:
        toast.error(message, toastParam);
        break;
      case TOAST_TYPE.SUCCESS:
        toast.success(message, toastParam);
      default:
        break;
    }
  };

  const onImageRemove = (index) => {
    const newFiles = [...images];
    newFiles.splice(index, 1);
    setImages(newFiles);
  };

  const onImageRemoveAll = () => {
    setImages([]);
  };

  const updateImageAt = (index, newFile) => {
    setImages((prevImages) => {
      const updatedImages = [...prevImages];
      updatedImages[index] = {
        file: newFile,
        data_url: URL.createObjectURL(newFile),
        fileId: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      };
      return updatedImages;
    });
  };

  const onImageUpdate = (index) => {
    setUpdatingFileIndex(index);
    open();
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (updatingFileIndex != null && acceptedFiles.length > 0) {
        updateImageAt(updatingFileIndex, acceptedFiles[0]);
        setUpdatingFileIndex(null);
      } else
        setImages((prevImages) => [
          ...prevImages,
          ...acceptedFiles.map((file) => ({
            file: file,
            data_url: URL.createObjectURL(file),
            fileId: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          })),
        ]);
    },
    [images, updatingFileIndex]
  );

  const onDropRejected = (filesRejected) => {
    let isMaxErrorShown = false;
    filesRejected.forEach((rejectedFile) => {
      rejectedFile.errors.forEach((error) => {
        switch (error.code) {
          case FILE_REJECTION_ERROR_CODE.FileInvalidType:
            showToast(
              intl.formatMessage(
                { id: 'fileUploader.invalidType' },
                {
                  fileName: rejectedFile.file.name,
                }
              ),
              TOAST_TYPE.ERROR
            );
            break;
          case FILE_REJECTION_ERROR_CODE.TooManyFiles:
            if (!isMaxErrorShown) {
              showToast(
                intl.formatMessage(
                  { id: 'fileUploader.maxFilesExceeded' },
                  {
                    maxCount: appConfig.fileUploader.maxNoOfFiles,
                  }
                ),
                TOAST_TYPE.ERROR
              );
              isMaxErrorShown = true;
            }
            break;
          default:
            break;
        }
      });
    });
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'application/octet-stream': allowedFileTypes,
    },
    onDrop,
    maxFiles: appConfig.fileUploader.maxNoOfFiles,
    noClick: true,
    noKeyboard: true,
    onDropRejected,
  });

  return (
    <div className="photo-uploader__container">
      <ToastContainer
        position="bottom-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      {!isMinimized && !isCollapsed && (
        <div className="photo-uploader__header-wrapper">
          <div className="photo-uploader__header-container">
            <h2 className="photo-uploader__header-container__header-text font-lg-medium">
              {intl.formatMessage({ id: 'fileUploader.subTitle' })}
            </h2>
            <p>
              {intl.formatMessage({ id: 'fileUploader.uploadToCategoryLabel' })}
              {categoryNameToUpload}
            </p>
          </div>
          <div
            className="photo-uploader__loader-close-icon"
            onClick={() => dispatch(hideCategoryFileUploader())}
          >
            <CloseIcon height={'30px'} width={'30px'} />
          </div>
        </div>
      )}

      <div className="photo-uploader__card">
        <div className="photo-uploader__card-wrapper" {...getRootProps()}>
          {(isJobProcessing || isUploadingIndicatorShown) && (
            <FilesLoadingIndicator
              fileList={images}
              setFileList={setImages}
              filesLoadingStatus={filesLoadingStatus}
              isUploadingIndicatorShown={isUploadingIndicatorShown}
              setIsUploadingIndicatorShown={setIsUploadingIndicatorShown}
              isFilesUploaded={isFilesUploaded}
              setIsFilesUploaded={setIsFilesUploaded}
              intl={intl}
            />
          )}
          {isError && (
            <div className="photo-uploader__loader-container">
              <div
                className="photo-uploader__loader-error-icon"
                onClick={homeScreenRefresh}
              >
                <CloseIcon height={'30px'} width={'30px'} />
              </div>
              <Error height={'40px'} width={'40px'} />
              <h5 className="font-md-regular ">
                {intl.formatMessage({ id: 'general.error' })}
                {intl.formatMessage({ id: 'general.tryAgain' })}
              </h5>
            </div>
          )}
          {/* //Drag & Drop area container */}
          <div className="photo-uploader__card-inner-wrap">
            {images.length != 0 && isDropAreaVisible && (
              <div
                className="photo-uploader__drop-container-close"
                onClick={() => setIsDropAreaVisible(false)}
              >
                <CloseIcon height={30} width={30} />
              </div>
            )}
            <input {...getInputProps()} />
            {isDropAreaVisible && (
              <div
                className={classNames('photo-uploader__drop-container')}
                style={isDragActive ? { borderColor: 'black' } : undefined}
                onClick={open}
              >
                <UploadBlack
                  fill={isDragActive ? '#5e2064' : '#dfdfdf'}
                  width={'40px'}
                  height={'40px'}
                  style={{ marginBottom: '5px' }}
                />
                <h4>
                  {isDragActive
                    ? intl.formatMessage({
                        id: 'fileUploader.dropAreaLabel',
                      })
                    : intl.formatMessage({
                        id: 'fileUploader.browseOrDropLabel',
                      })}
                </h4>
                <div
                  className={classNames(
                    'photo-uploader__drop-container-subtext'
                  )}
                >
                  {intl.formatMessage({
                    id: 'fileUploader.supportedFileTypes',
                  })}
                  [ {Array.from(new Set(allowedFileTypes)).join(', ')} ]
                </div>
              </div>
            )}
            {/* Images List */}
            <FilesList
              imageList={images}
              onImageRemove={onImageRemove}
              onImageUpdate={onImageUpdate}
              setIsDropAreaVisible={setIsDropAreaVisible}
              onImageRemoveAll={onImageRemoveAll}
              metaTags={metaTags}
              maxFileSize={appConfig.fileUploader.maxTotalFilesSize}
              hstPath={hstPath}
              categoryIdToUpload={categoryIdToUpload}
              surl={surl}
              csrf={csrf}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
              completedApiCount={completedApiCount}
              setCompletedApiCount={setCompletedApiCount}
              setIsError={setIsError}
              showToast={showToast}
              setIsFilesUploaded={setIsFilesUploaded}
              metaValues={metaValues}
              setMetaValues={setMetaValues}
              setIsJobProcessing={setIsJobProcessing}
              isJobProcessing={isJobProcessing}
              isAllJobsFinished={isAllJobsFinished}
              setIsAllJobsFinished={setIsAllJobsFinished}
              filesLoadingStatus={filesLoadingStatus}
              setFilesLoadingStatus={setFilesLoadingStatus}
              setIsUploadingIndicatorShown={setIsUploadingIndicatorShown}
              intl={intl}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(FilesUploader);
